/**
 * DatasetFormDialog — Inteligência de Dados (Fase 2)
 * -----------------------------------------------------------------------------
 * Modal de criação/edição de um DATASET EXTERNO: define de qual conexão e de
 * qual operação os dados serão importados, e como identificar cada registro
 * (chave de negócio ou hash do conteúdo).
 *
 * Só aparecem operações marcadas como importáveis no catálogo do provider.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 2 - Dados Externos).
 */

import { useEffect, useMemo, useState } from 'react';
import { Database, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIntegrationConnections } from '@/hooks/integrations/useIntegrationConnections';
import {
  useExternalDatasets,
  type ExternalDataset,
} from '@/hooks/data/useExternalDatasets';

interface DatasetFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Dataset em edição (null = criação) */
  dataset?: ExternalDataset | null;
}

/** Modal de configuração de dataset externo */
export function DatasetFormDialog({ open, onOpenChange, dataset }: DatasetFormDialogProps) {
  const { providers, connections } = useIntegrationConnections();
  const { saveDataset } = useExternalDatasets();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [operationCode, setOperationCode] = useState('');
  const [importMode, setImportMode] = useState('FULL_REFRESH');
  const [identityStrategy, setIdentityStrategy] = useState('HASH');
  const [identityFields, setIdentityFields] = useState('');

  /** Carrega o estado do formulário sempre que o modal abre */
  useEffect(() => {
    if (!open) return;
    setName(dataset?.name ?? '');
    setDescription(dataset?.description ?? '');
    setConnectionId(dataset?.connection_id ?? '');
    setOperationCode(dataset?.operation_code ?? '');
    setImportMode(dataset?.import_mode ?? 'FULL_REFRESH');
    setIdentityStrategy(dataset?.identity_strategy ?? 'HASH');
    setIdentityFields((dataset?.identity_fields ?? []).join(', '));
  }, [open, dataset]);

  /** Conexões ativas disponíveis para importação */
  const activeConnections = useMemo(
    () => connections.filter((c) => c.is_active),
    [connections],
  );

  /** Operações importáveis do provider da conexão escolhida */
  const operations = useMemo(() => {
    const connection = connections.find((c) => c.id === connectionId);
    if (!connection) return [];
    const provider = providers.find((p) => p.slug === connection.provider_slug);
    return (provider?.operations ?? []).filter((op) => op.is_importable);
  }, [connectionId, connections, providers]);

  /** Ao trocar de conexão, a operação anterior deixa de ser válida */
  const handleConnectionChange = (value: string) => {
    setConnectionId(value);
    setOperationCode('');
  };

  const canSubmit =
    name.trim().length > 1 &&
    Boolean(connectionId) &&
    Boolean(operationCode) &&
    (identityStrategy !== 'BUSINESS_KEY' || identityFields.trim().length > 0);

  /** Salva o dataset via RPC */
  const handleSubmit = async () => {
    if (!canSubmit) return;
    await saveDataset.mutateAsync({
      datasetId: dataset?.id ?? null,
      name: name.trim().slice(0, 120),
      description: description.trim().slice(0, 500) || null,
      connectionId,
      operationCode,
      importMode,
      identityStrategy,
      identityFields:
        identityStrategy === 'BUSINESS_KEY'
          ? identityFields
              .split(',')
              .map((f) => f.trim())
              .filter(Boolean)
              .slice(0, 5)
          : [],
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] p-0 gap-0 overflow-hidden">
        <PremiumDialogHeader
          icon={Database}
          title={dataset ? 'Editar dataset' : 'Novo dataset'}
          description="Defina a origem dos dados e como cada registro é identificado"
        />

        <div className="space-y-4 p-5 max-h-[70vh] overflow-y-auto">
          {/* Identificação */}
          <div className="space-y-1.5">
            <Label htmlFor="dataset-name">Nome</Label>
            <Input
              id="dataset-name"
              value={name}
              maxLength={120}
              placeholder="Ex.: Associados ativos (SGA)"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="dataset-description">Descrição (opcional)</Label>
            <Textarea
              id="dataset-description"
              value={description}
              maxLength={500}
              rows={2}
              placeholder="Para que serve este conjunto de dados"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Origem */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Conexão</Label>
              <Select value={connectionId} onValueChange={handleConnectionChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a conexão" />
                </SelectTrigger>
                <SelectContent>
                  {activeConnections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name} · {c.provider_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Operação</Label>
              <Select
                value={operationCode}
                onValueChange={setOperationCode}
                disabled={!connectionId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a operação" />
                </SelectTrigger>
                <SelectContent>
                  {operations.map((op) => (
                    <SelectItem key={op.code} value={op.code}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {activeConnections.length === 0 && (
            <InlineNotice variant="warning">
              Nenhuma conexão ativa. Cadastre uma conexão em Inteligência de Dados › Conexões antes
              de criar um dataset.
            </InlineNotice>
          )}

          {/* Estratégia de importação */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Modo de importação</Label>
              <Select value={importMode} onValueChange={setImportMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FULL_REFRESH">Recarga completa</SelectItem>
                  <SelectItem value="INCREMENTAL">Incremental</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Identidade do registro</Label>
              <Select value={identityStrategy} onValueChange={setIdentityStrategy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HASH">Hash do conteúdo</SelectItem>
                  <SelectItem value="BUSINESS_KEY">Chave de negócio</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {identityStrategy === 'BUSINESS_KEY' && (
            <div className="space-y-1.5">
              <Label htmlFor="dataset-identity">Campos da chave (separados por vírgula)</Label>
              <Input
                id="dataset-identity"
                value={identityFields}
                maxLength={200}
                placeholder="Ex.: id, cpf, placa"
                onChange={(e) => setIdentityFields(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Até 5 campos. Registros com a mesma chave são atualizados em vez de duplicados.
              </p>
            </div>
          )}

          <InlineNotice variant="info">
            A importação roda em segundo plano, em páginas, para não impactar a performance do
            sistema. Acompanhe o andamento em Histórico de sincronização.
          </InlineNotice>
        </div>

        <DialogFooter className="border-t p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || saveDataset.isPending}>
            {saveDataset.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
